import { Box, Card, Link, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router';
import { DataTable, type Column } from '../../components/DataTable';
import { ErrorState, PageHeader } from '../../components/states';
import { Panel } from '../../components/ui';
import { dateTime, money } from '../../lib/format';
import {
  fetchPluginRevenue,
  PERIOD_LABELS,
  pluginKeys,
  PLUGINS,
  type PluginName,
  type PluginRevenue,
} from './api';

type Row = PluginRevenue['periods'][number];

const monthLabel = (m: string) =>
  new Date(`${m}-01T00:00:00Z`).toLocaleDateString('en-GB', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });

/** What shops paid for an add-on (Foodpanda, Email), apart from subscription revenue. */
export function PluginRevenuePage({ plugin }: { plugin: PluginName }) {
  const { label } = PLUGINS[plugin];
  const q = useQuery({
    queryKey: pluginKeys.revenue(plugin),
    queryFn: () => fetchPluginRevenue(plugin),
  });
  const columns: Column<Row>[] = [
    { key: 'when', header: 'When', render: (p) => dateTime(p.createdAt) },
    {
      key: 'shop',
      header: 'Shop',
      render: (p) => (
        <Link component={RouterLink} to={`/tenants/${p.tenant.id}?tab=${plugin}`}>
          {p.tenant.name}
        </Link>
      ),
    },
    { key: 'kind', header: 'Change', render: (p) => PERIOD_LABELS[p.kind] },
    {
      key: 'period',
      header: 'Period',
      render: (p) => (p.months ? `${String(p.months)} mo` : p.days ? `${String(p.days)} d` : '—'),
      hideBelow: 'sm',
    },
    { key: 'by', header: 'By', render: (p) => p.admin.name, hideBelow: 'md' },
    {
      key: 'amount',
      header: 'Charged',
      align: 'right',
      render: (p) => (p.amount === null ? '—' : money(p.amount)),
    },
  ];
  return (
    <>
      <PageHeader
        title={`${label} revenue`}
        subtitle={`What shops paid for the ${label} add-on, kept apart from subscriptions.`}
      />
      {q.isError ? (
        <Card>
          <ErrorState error={q.error} onRetry={() => void q.refetch()} />
        </Card>
      ) : (
        <Stack spacing={2.5}>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(200px, 1fr))' },
            }}
          >
            <Card sx={{ p: 2.5 }}>
              <Typography variant="caption" color="text.secondary">
                Total charged
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {q.data ? money(q.data.total) : '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {q.data ? `${String(q.data.count)} payments` : ''}
              </Typography>
            </Card>
            {q.data?.byMonth.slice(0, 5).map((m) => (
              <Card key={m.month} sx={{ p: 2.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {monthLabel(m.month)}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {money(m.total)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {m.count} payments
                </Typography>
              </Card>
            ))}
          </Box>
          <Panel title="Payments" padded={false}>
            <DataTable
              columns={columns}
              rows={q.data?.periods.filter((p) => p.amount !== null)}
              rowKey={(p) => p.id}
              loading={q.isPending}
              empty={<Box sx={{ p: 3, color: 'text.secondary' }}>Nothing charged yet.</Box>}
            />
          </Panel>
        </Stack>
      )}
    </>
  );
}
